import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import {
  ArrowLeft,
  LoaderCircle,
  Plus,
  Save,
  Trash2,
} from "lucide-react";
import { Button } from "../ui/button";
import { FieldError } from "../ui/field";
import { Input } from "../ui/input";
import ImageUploadGuidance from "../common/ImageUploadGuidance";
import InlineFormFileUpload, { IMAGE_TYPES } from "../file-upload/InlineFormFileUpload";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import useToast from "../../hooks/useToast";
import apiClient from "../../lib/utils/network-client";
import SeoFields from "../seo/SeoFields";

const EMPTY_FORM = {
  title: "",
  main_banners: [],
  sub_description: "",
  category: "",
  description: "",
  brochure: null,
  services_intro: "",
  services_heading: "",
  services: [{ title: "" }],
  departments: [{ name: "", description: "" }],
  meta_title: "",
  meta_description: "",
};

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["link", "blockquote"],
    ["clean"],
  ],
};

const isMeaningfulHtml = (html) =>
  html && html.replace(/<(.|\n)*?>/g, "").replace(/&nbsp;/g, " ").trim().length > 0;

const createBlankService = () => ({ title: "" });
const createBlankDepartment = () => ({
  name: "",
  description: "",
  _key: `department-${Date.now()}-${Math.random()}`,
});

const SpecialityFormPage = ({ mode }) => {
  const navigate = useNavigate();
  const { specialityId } = useParams();
  const { showErrorToast, showSuccessToast } = useToast();
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [isPageLoading, setIsPageLoading] = useState(mode === "edit");
  const [isSaving, setIsSaving] = useState(false);
  const [existingMainBanners, setExistingMainBanners] = useState([]);
  const [replacedMainBannerIds, setReplacedMainBannerIds] = useState([]);
  const [brochurePreviewUrl, setBrochurePreviewUrl] = useState("");
  const [brochureType, setBrochureType] = useState("");

  const isEditMode = mode === "edit";

  const pageTitle = useMemo(
    () => (isEditMode ? "Edit Speciality" : "Create Speciality"),
    [isEditMode],
  );

  useEffect(() => {
    if (!isEditMode) {
      setIsPageLoading(false);
      return;
    }

    const fetchSpeciality = async () => {
      setIsPageLoading(true);
      try {
        const response = await apiClient.get(`/specialities/${specialityId}`);
        const item = response.data?.data;
        if (!item) {
          showErrorToast("Speciality not found");
          navigate("/specialities");
          return;
        }

        setForm({
          title: item.title || "",
          main_banners: [],
          sub_description: item.sub_description || "",
          category: item.category || "",
          description: item.description || "",
          brochure: null,
          services_intro: item.services_intro || "",
          services_heading: item.services_heading || "",
          services: item.services?.length
            ? item.services.map((service) => ({ title: service.title || "" }))
            : [{ title: "" }],
          departments: item.departments?.length
            ? item.departments.map((department) => ({
                id: department.id,
                name: department.name || "",
                description: department.description || "",
              }))
            : [createBlankDepartment()],
          meta_title: item.meta_title || "",
          meta_description: item.meta_description || "",
        });
        setExistingMainBanners(item.main_banners || []);
        setReplacedMainBannerIds([]);
        setBrochurePreviewUrl(item.brochure_url || "");
        setBrochureType(item.brochure_type || "");
      } catch (err) {
        showErrorToast(err.response?.data?.message || "Failed to load speciality");
        navigate("/specialities");
      } finally {
        setIsPageLoading(false);
      }
    };

    fetchSpeciality();
  }, [isEditMode, navigate, showErrorToast, specialityId]);

  const handleInputChange = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
    setFormErrors((current) => ({
      ...current,
      [field]: "",
    }));
  };

  const handleServiceChange = (index, value) => {
    setForm((current) => ({
      ...current,
      services: current.services.map((service, serviceIndex) =>
        serviceIndex === index ? { ...service, title: value } : service,
      ),
    }));
    setFormErrors((current) => ({
      ...current,
      services: current.services?.map((item, itemIndex) =>
        itemIndex === index ? { ...item, title: "" } : item,
      ),
      services_message: "",
    }));
  };

  const handleAddService = () => {
    setForm((current) => ({
      ...current,
      services: [...current.services, createBlankService()],
    }));
  };

  const handleRemoveService = (index) => {
    setForm((current) => ({
      ...current,
      services: current.services.filter((_, serviceIndex) => serviceIndex !== index),
    }));
    setFormErrors((current) => ({
      ...current,
      services: current.services?.filter((_, serviceIndex) => serviceIndex !== index),
    }));
  };

  const handleDepartmentChange = (index, field, value) => {
    setForm((current) => ({
      ...current,
      departments: current.departments.map((department, departmentIndex) =>
        departmentIndex === index
          ? { ...department, [field]: value }
          : department,
      ),
    }));
    setFormErrors((current) => ({
      ...current,
      departments: current.departments?.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: "" } : item,
      ),
    }));
  };

  const handleAddDepartment = () => {
    setForm((current) => ({
      ...current,
      departments: [...current.departments, createBlankDepartment()],
    }));
  };

  const handleRemoveDepartment = (index) => {
    setForm((current) => ({
      ...current,
      departments:
        current.departments.length === 1
          ? [createBlankDepartment()]
          : current.departments.filter((_, departmentIndex) => departmentIndex !== index),
    }));
    setFormErrors((current) => ({
      ...current,
      departments:
        current.departments?.length === 1
          ? []
          : current.departments?.filter((_, departmentIndex) => departmentIndex !== index),
    }));
  };

  const handleRemoveExistingMainBanner = (banner) => {
    if (!banner?.id) return;
    setExistingMainBanners((current) =>
      current.filter((item) => item.id !== banner.id),
    );
    setReplacedMainBannerIds((current) =>
      current.filter((id) => id !== banner.id),
    );
  };

  const handleStageMainBannerReplacement = (banner) => {
    if (!banner?.id) return;
    setReplacedMainBannerIds((current) =>
      current.includes(banner.id) ? current : [...current, banner.id],
    );
  };

  const handleCancelMainBannerReplacement = (banner) => {
    if (!banner?.id) return;
    setReplacedMainBannerIds((current) =>
      current.filter((id) => id !== banner.id),
    );
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!form.title.trim()) nextErrors.title = "Title is required.";
    if (!form.sub_description.trim()) nextErrors.sub_description = "Sub description is required.";
    if (!form.category) nextErrors.category = "Category is required.";
    if (!isMeaningfulHtml(form.description)) nextErrors.description = "Description is required.";
    if (!isEditMode && !form.brochure) nextErrors.brochure = "Brochure is required.";

    const populatedServices = form.services.filter((item) => item.title.trim());
    if (form.services.some((item) => !item.title.trim()) && populatedServices.length > 0) {
      nextErrors.services = form.services.map((item) => (!item.title.trim() ? { title: "Title is required." } : {}));
    }

    const departmentErrors = form.departments.map((department) => {
      const hasName = Boolean(department.name.trim());
      const hasDescription = Boolean(isMeaningfulHtml(department.description));
      const hasStartedDepartment = hasName || hasDescription;

      return {
        name: hasStartedDepartment && !hasName ? "Department name is required." : "",
        description:
          hasStartedDepartment && !hasDescription
            ? "Department description is required."
            : "",
      };
    });

    if (departmentErrors.some((item) => item.name || item.description)) {
      nextErrors.departments = departmentErrors;
    }

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const createFormData = () => {
    const payload = new FormData();
    payload.append("title", form.title.trim());
    payload.append("sub_description", form.sub_description.trim());
    payload.append("category", form.category);
    payload.append("description", form.description);
    payload.append("services_intro", form.services_intro.trim());
    payload.append("services_heading", form.services_heading.trim());
    payload.append("meta_title", form.meta_title.trim());
    payload.append("meta_description", form.meta_description.trim());
    payload.append(
      "services",
      JSON.stringify(
        form.services
          .filter((item) => item.title.trim())
          .map((item, index) => ({
            title: item.title.trim(),
            display_order: index,
          })),
      ),
    );
    payload.append(
      "departments",
      JSON.stringify(
        form.departments
          .filter(
            (item) => item.name.trim() && isMeaningfulHtml(item.description),
          )
          .map((item, index) => ({
            name: item.name.trim(),
            description: item.description,
            display_order: index,
          })),
      ),
    );

    form.main_banners.forEach((file) => {
      payload.append("main_banners", file);
    });

    if (isEditMode) {
      const replacedIds = new Set(replacedMainBannerIds);
      payload.append(
        "retained_main_banner_ids",
        JSON.stringify(
          existingMainBanners
            .filter((item) => item.id && !replacedIds.has(item.id))
            .map((item) => item.id),
        ),
      );
    }

    if (form.brochure) {
      payload.append("brochure", form.brochure);
    }

    return payload;
  };

  const handleSave = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      const payload = createFormData();

      if (isEditMode) {
        await apiClient.put(`/specialities/${specialityId}`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showSuccessToast("Speciality updated successfully");
      } else {
        await apiClient.post("/specialities", payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showSuccessToast("Speciality created successfully");
      }

      navigate("/specialities");
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Unable to save speciality");
    } finally {
      setIsSaving(false);
    }
  };

  if (isPageLoading) {
    return (
      <div className="rounded-lg border border-white/60 bg-white/80 p-8 shadow-sm">
        <div className="flex items-center gap-3 text-sm font-medium text-slate-500">
          <LoaderCircle className="h-4 w-4 animate-spin" />
          Loading speciality editor...
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] space-y-6">
      <div>
        <Button type="button" variant="ghost" className="-ml-3 mb-2 rounded-lg px-3 text-slate-500" onClick={() => navigate("/specialities")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Specialities
        </Button>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">{pageTitle}</h1>
      </div>

      <form onSubmit={handleSave} className="rounded-lg border border-white/60 bg-white/80 p-6 shadow-sm backdrop-blur-xl">
        <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Title</label>
              <Input value={form.title} onChange={(event) => handleInputChange("title", event.target.value)} className={`rounded-lg ${formErrors.title ? "border-red-300 focus-visible:ring-red-500" : ""}`} />
              <FieldError>{formErrors.title}</FieldError>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Sub Description</label>
              <textarea
                value={form.sub_description}
                onChange={(event) => handleInputChange("sub_description", event.target.value)}
                rows={5}
                className={`min-h-[140px] w-full rounded-lg border bg-white px-4 py-3 text-sm text-slate-700 outline-none ${
                  formErrors.sub_description
                    ? "border-red-300 focus:ring-2 focus:ring-red-500"
                    : "border-slate-200 focus:ring-2 focus:ring-primary/30"
                }`}
              />
              <FieldError>{formErrors.sub_description}</FieldError>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Category</label>
              <Select value={form.category} onValueChange={(value) => handleInputChange("category", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="super">Super</SelectItem>
                </SelectContent>
              </Select>
              <FieldError>{formErrors.category}</FieldError>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Description</label>
              <div className={`overflow-hidden rounded-lg border bg-white ${formErrors.description ? "border-red-300" : "border-slate-200"}`}>
                <ReactQuill
                  theme="snow"
                  value={form.description}
                  onChange={(value) => handleInputChange("description", value)}
                  modules={quillModules}
                  placeholder="Write speciality description here..."
                />
              </div>
              <FieldError>{formErrors.description}</FieldError>
            </div>

            <div className="space-y-4 rounded-lg border border-slate-200 bg-slate-50/70 p-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Departments</h2>
                  <p className="text-sm text-slate-500">
                    Add department names and the rich-text content shown on the speciality page.
                  </p>
                </div>
                <Button type="button" variant="outline" className="rounded-lg" onClick={handleAddDepartment}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add More
                </Button>
              </div>

              <div className="space-y-5">
                {form.departments.map((department, index) => {
                  const departmentError = formErrors.departments?.[index] || {};

                  return (
                    <div
                      key={department.id || department._key || `department-${index + 1}`}
                      className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
                    >
                      <div className="mb-4 flex items-center justify-between gap-3">
                        <h3 className="text-sm font-semibold text-slate-700">
                          Department {index + 1}
                        </h3>
                        <Button
                          type="button"
                          variant="outline"
                          className="rounded-lg border-red-200 text-red-600 hover:bg-red-50"
                          onClick={() => handleRemoveDepartment(index)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Remove
                        </Button>
                      </div>

                      <div className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-slate-700">Name</label>
                          <Input
                            value={department.name}
                            onChange={(event) =>
                              handleDepartmentChange(index, "name", event.target.value)
                            }
                            className={`rounded-lg ${departmentError.name ? "border-red-300 focus-visible:ring-red-500" : ""}`}
                            placeholder="Department name"
                          />
                          <FieldError>{departmentError.name}</FieldError>
                        </div>

                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-slate-700">Description</label>
                          <div className={`overflow-hidden rounded-lg border bg-white ${departmentError.description ? "border-red-300" : "border-slate-200"}`}>
                            <ReactQuill
                              theme="snow"
                              value={department.description}
                              onChange={(value) =>
                                handleDepartmentChange(index, "description", value)
                              }
                              modules={quillModules}
                              placeholder="Write department description here..."
                            />
                          </div>
                          <FieldError>{departmentError.description}</FieldError>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Services Description</label>
              <textarea
                value={form.services_intro}
                onChange={(event) => handleInputChange("services_intro", event.target.value)}
                rows={3}
                className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="Paragraph shown below the Services title"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Extra Services Heading</label>
              <Input
                value={form.services_heading}
                onChange={(event) => handleInputChange("services_heading", event.target.value)}
                className="rounded-lg"
                placeholder="Heading shown above the bullet list"
              />
            </div>

            <div className="space-y-4 rounded-lg border border-slate-200 bg-slate-50/70 p-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Extra Services Bullet Points</h2>
                  <p className="text-sm text-slate-500">Add services that should appear as bullet points below the main services description.</p>
                </div>
                <Button type="button" variant="outline" className="rounded-lg" onClick={handleAddService}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Service
                </Button>
              </div>
              <FieldError>{formErrors.services_message}</FieldError>

              <div className="space-y-4">
                {form.services.map((service, index) => {
                  const serviceError = formErrors.services?.[index] || {};

                  return (
                    <div key={`service-${index + 1}`} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                      <div className="mb-4 flex items-center justify-between gap-3">
                        <h3 className="text-sm font-semibold text-slate-700">Service {index + 1}</h3>
                        <Button
                          type="button"
                          variant="outline"
                          className="rounded-lg border-red-200 text-red-600 hover:bg-red-50"
                          onClick={() => handleRemoveService(index)}
                          disabled={form.services.length === 1}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Remove
                        </Button>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-slate-700">Title</label>
                        <Input
                          value={service.title}
                          onChange={(event) => handleServiceChange(index, event.target.value)}
                          className={`rounded-lg ${serviceError.title ? "border-red-300 focus-visible:ring-red-500" : ""}`}
                        />
                        <FieldError>{serviceError.title}</FieldError>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <InlineFormFileUpload
                label="Speciality Images"
                value={form.main_banners}
                onChange={(files) => handleInputChange("main_banners", files)}
                existingFiles={existingMainBanners}
                onRemoveExisting={handleRemoveExistingMainBanner}
                onStageExistingReplacement={handleStageMainBannerReplacement}
                onCancelExistingReplacement={handleCancelMainBannerReplacement}
                accept={IMAGE_TYPES}
                maxSizeBytes={10 * 1024 * 1024}
                maxFiles={10}
                countExistingTowardsMax
                required={false}
                error={formErrors.main_banners}
                helperText="Optional. Add, replace, or remove up to 10 JPG, PNG, GIF or WEBP images — maximum 10 MB each. The first retained image is used as the primary speciality image. If no image remains after save, no image is shown on the public site."
              />
              <ImageUploadGuidance
                requirementKey="specialityMain"
                files={form.main_banners}
                sources={existingMainBanners}
              />
            </div>

            <div className="space-y-2">
              <InlineFormFileUpload
                label="Brochure"
                value={form.brochure}
                onChange={(file) => handleInputChange("brochure", file)}
                existingFiles={brochurePreviewUrl ? [{
                  name: "Current brochure",
                  url: brochurePreviewUrl,
                  mimeType: brochureType === "pdf" ? "application/pdf" : "image/jpeg",
                }] : []}
                accept={["application/pdf", ...IMAGE_TYPES]}
                maxSizeBytes={10 * 1024 * 1024}
                required={!isEditMode}
                error={formErrors.brochure}
                helperText="PDF, JPG, PNG, GIF or WEBP — maximum 10 MB"
              />
              <ImageUploadGuidance
                requirementKey="specialityBrochure"
                file={form.brochure?.type?.startsWith("image/") ? form.brochure : null}
                src={brochureType === "image" ? brochurePreviewUrl : ""}
              />
            </div>
          </div>
        </div>

        <SeoFields
          metaTitle={form.meta_title}
          metaDescription={form.meta_description}
          onChange={handleInputChange}
          className="mt-6"
        />

        <div className="mt-6 flex flex-col gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" className="rounded-lg" onClick={() => navigate("/specialities")}>
            Cancel
          </Button>
          <Button type="submit" className="rounded-lg" disabled={isSaving}>
            {isSaving ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            {isSaving ? "Saving..." : isEditMode ? "Update Speciality" : "Create Speciality"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SpecialityFormPage;
